import { useState } from 'react';
import { cx } from 'class-variance-authority';

import { Button, Icon, Popover } from '@nvision/ui';

import styles from './context-actions.module.css';

export type ContextActionType = 'default' | 'primary' | 'danger';

export type ContextAction = {
  label: string;
  onClick: () => void | Promise<void>; // allows async handling
  type: ContextActionType; // for styling
  disabled?: boolean;
  separator?: boolean;
};

interface ContextActionsProps {
  actions: ContextAction[];
  target?: React.ReactElement<{
    className?: string;
  }>;
}

const BUTTON_TYPES_MAP: Record<ContextActionType, Partial<Record<'secondary' | 'primary' | 'destructive', true>>> = {
  danger: { destructive: true },
  default: { secondary: true },
  primary: { primary: true },
} as const;

export const ContextActions: React.FC<ContextActionsProps> = ({ actions, target }) => {
  const [processingAction, setProcessingAction] = useState<string | null>(null);

  const onClick = async (action: ContextAction) => {
    try {
      setProcessingAction(action.label);
      await action.onClick();
    } finally {
      setProcessingAction(null);
    }
  };

  return (
    <Popover>
      <Popover.Target>{target ?? <Icon name="ellipsis-vertical" className={styles.menuIcon} />}</Popover.Target>
      <Popover.Content className={styles.menuContent}>
        {actions.map((action) => (
          <Button
            key={action.label}
            {...BUTTON_TYPES_MAP[action.type]}
            ghost
            className={cx(styles.menuItem, { [styles.separatedItem]: action.separator })}
            onClick={() => onClick(action)}
            isLoading={processingAction === action.label}
          >
            {action.label}
          </Button>
        ))}
      </Popover.Content>
    </Popover>
  );
};
